import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  FieldPath,
  FieldValues,
  PathValue,
  useController,
  useFormContext,
} from "react-hook-form";

import { BaseFieldProps, DependsOn, Options } from "./FieldProps";
import Text from "./Text";
import useIsFieldDisabled from "../../hooks/formHooks/useIsFieldDisabled";
import { mono } from "../../theme/colors";
import ArrowDownIcon from "../icons/arrow/ArrowDownIcon";

type Props<TFieldValues extends FieldValues> = {
  options: Options;
  dependsOn?: DependsOn<TFieldValues>;
} & BaseFieldProps<TFieldValues>;

const MultiSelect = <TFieldValues extends FieldValues>({
  name,
  label,
  disabled = false,
  disabledForEdit = false,
  options,
  dependsOn,
}: Props<TFieldValues>) => {
  const { control, clearErrors, watch, setError } =
    useFormContext<TFieldValues>();
  const { field, fieldState } = useController({
    name,
    control,
  });
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    field.value ?? [],
  );

  // TODO: This logic is duplicated extract into a HOC
  const valueToWatch = dependsOn?.field && watch(dependsOn?.field);
  const shouldValidate = !!dependsOn;
  const shouldEnable = !shouldValidate || valueToWatch === dependsOn.value;
  const fieldIsDisabled = useIsFieldDisabled(disabled, disabledForEdit);
  const errorMessage = fieldState.error?.message;

  useEffect(() => {
    if (!shouldEnable) {
      if (selectedOptions.length) {
        setSelectedOptions([]);
      }
      field.onChange([] as PathValue<TFieldValues, FieldPath<TFieldValues>>);
      clearErrors(name);
    }
    if (shouldEnable && shouldValidate) {
      if (
        dependsOn.required &&
        (!selectedOptions || selectedOptions?.length === 0)
      ) {
        setError(name, {
          message: dependsOn.message,
          type: "required",
        });
      } else {
        clearErrors(name);
      }
    }
  }, [shouldEnable, shouldValidate, name, selectedOptions]);

  const handleChange = (event: SelectChangeEvent<typeof selectedOptions>) => {
    const {
      target: { value },
    } = event;
    setSelectedOptions(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value,
    );
    field.onChange(value as PathValue<TFieldValues, FieldPath<TFieldValues>>);
    clearErrors(name);
  };

  return (
    <FormControl
      disabled={!shouldEnable || fieldIsDisabled}
      error={!!errorMessage}
      sx={{
        minWidth: 150,
        backgroundColor: mono.white,
      }}
    >
      <InputLabel id={`${label}-multi-select-checkbox-label`}>
        <Text variant={"body_small"}>{label}</Text>
      </InputLabel>
      <Select
        data-testid={`form-${label}-SELECT_MULTI`}
        labelId={`${label}-multi-select-checkbox-label`}
        id={`${label}-multi-select-checkbox`}
        multiple
        value={selectedOptions}
        onChange={handleChange}
        renderValue={selected => {
          const valueMappedToLabel = selected.map(
            selected =>
              options.find(option => option.value === selected)?.label,
          );
          return (
            <Text variant={"body_small"} textTransform={"capitalize"}>
              {valueMappedToLabel.join(", ")}
            </Text>
          );
        }}
        IconComponent={props => (
          <Box {...props}>
            <ArrowDownIcon />
          </Box>
        )}
      >
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={selectedOptions.indexOf(option.value) > -1} />
            <Text variant={"button"} textTransform={"capitalize"}>
              {option.label}
            </Text>
          </MenuItem>
        ))}
      </Select>
      {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default MultiSelect;
